import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

export default function VerTarjeta({
  tarjeta: tarjetaProp = null,
  onClose = () => {},
}) {
  const [data, setData] = useState(tarjetaProp);
  const [productos, setProductos] = useState([]);
  const [eess, setEess] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guard para evitar doble fetch en dev (StrictMode)
  const devOnceRef = useRef({ key: null, pass: 0 });

  useEffect(() => {
    setData(tarjetaProp);
    setProductos([]);
    setEess([]);
    setError(null);

    const id = tarjetaProp?.id;
    if (!id) return;

    // saltear la 1ª pasada por cada id en desarrollo
    if (process.env.NODE_ENV !== "production") {
      const key = String(id);
      if (devOnceRef.current.key !== key) {
        devOnceRef.current = { key, pass: 0 };
      }
      if (devOnceRef.current.pass === 0) {
        devOnceRef.current.pass = 1;
        return;
      }
    }

    const ac = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const [tarjetaJson, prodJson, eessJson] = await Promise.all([
          apiFetchJson(apiRoutes.tarjetas.detail(id), { signal: ac.signal }),
          apiFetchJson(
            apiRoutes.productoTarjeta.asignados(id),
            { signal: ac.signal }
          ),
          apiFetchJson(
            apiRoutes.eessHabilitadas.asignadas(id),
            { signal: ac.signal }
          ),
        ]);

        if (ac.signal.aborted) return;

        setData(tarjetaJson);
        setProductos(prodJson?.items ?? []);
        setEess(eessJson?.items ?? []);
      } catch (e) {
        if (ac.signal.aborted) return;
        setError(e?.message || "Error al cargar los datos");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [tarjetaProp?.id]);

  const safe = (v, fallback = "—") =>
    v === null || v === undefined || v === "" ? fallback : v;

  //Nuevo
  const ultimoKmValue = data?.numeroUltimoKm ?? data?.ultimoKm;

  const grid = [
    ["Nro. de Tarjeta", safe(data?.numero)],
    ["Tarjeta Anterior", safe(data?.tarjetaAnterior)],
    ["Cod. Cliente", safe(data?.clienteCodigo)],
    ["Cliente", safe(data?.clienteNombre)],
    ["Estado", safe(data?.estadoNombre)],
    ["Chapa", safe(data?.chapa)],
    ["Marca", safe(data?.vehiculoMarca)],
    ["Modelo", safe(data?.vehiculoModelo)],
    ["Centro Costo", safe(data?.dataCentroCosto)],
    ["Usuario Solicitud", safe(data?.usuarioSolicitudCodigo)],//usuarioSolicitudCodigo
    ["Capacidad Tanque", safe(data?.capacidadTanque)],
    ["Último KM", safe(ultimoKmValue)],
    ["Tipo", safe(data?.codigoTipo)],
    ["Fecha Alta", safe(data?.dataFechaAlta)],
    ["Fecha Baja", safe(data?.dataFechaBaja)],
    ["Fecha Vigencia Inicio", safe(data?.dataFechaVigenciaIni)],
    ["Fecha Vigencia Final", safe(data?.dataFechaVigenciaFin)],
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-6 overflow-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h2 className="section-title">Datos Tarjeta</h2>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6">
          {loading ? (
            <div className="text-center text-gray-500 py-6">Cargando...</div>
          ) : error ? (
            <div className="text-center text-red-600 py-6">{error}</div>
          ) : (
            <>
              {/* Datos generales */}
              <div className="grid grid-cols-1 gap-y-3">
                {grid.map(([label, val]) => (
                  <Fila key={label} label={label} value={val} />
                ))}
              </div>

              {/* EE.SS. asignadas */}
              <Seccion titulo="Estaciones de Servicio Asignadas">
                {eess.length === 0 ? (
                  <div className="text-sm text-gray-500">Sin EE.SS. asignadas</div>
                ) : (
                  <ul className="list-disc pl-6 space-y-1">
                    {eess.map((e) => (
                      <li key={e.id}>{e.label ?? e.nombre}</li>
                    ))}
                  </ul>
                )}
              </Seccion>

              {/* Productos asignados */}
              <Seccion titulo="Productos Asignados">
                {productos.length === 0 ? (
                  <div className="text-sm text-gray-500">Sin productos asignados</div>
                ) : (
                  <ul className="list-disc pl-6 space-y-1">
                    {productos.map((p) => (
                      <li key={p.id}>{p.label ?? p.nombre}</li>
                    ))}
                  </ul>
                )}
              </Seccion>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
          >
            Nueva Consulta
          </button>
        </div>
      </div>
    </div>
  );
}

function Fila({ label, value }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-y-2">
      <div className="label">{label}:</div>
      <div className="value">{value}</div>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div className="border rounded-md p-4 bg-gray-50">
      <div className="font-semibold mb-2">{titulo}</div>
      {children}
    </div>
  );
}
